import { Component, Inject, OnInit, inject, signal } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MerchantService } from '@galaxy/billing';
import { TranslateService } from '@ngx-translate/core';
import { PaymentLinkItem } from '@vendasta/billing';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { getCurrencySymbol } from '@vendasta/galaxy/utility/currency-utils';
import { catchError, switchMap, take } from 'rxjs/operators';
import { InboxService } from '../../../../../../core/src/lib/inbox.service';
import { CONVERSATION_HOST_APP_INTERFACE_TOKEN, PARTNER_ID_TOKEN } from '../../../../../../core/src/lib/tokens';
import { of } from 'rxjs';
import { Validator } from '@vendasta/shared';
import moment from 'moment/moment';

@Component({
  selector: 'inbox-insert-payment-link-form',
  templateUrl: 'inbox-insert-payment-link-form.component.html',
  styleUrls: ['inbox-insert-payment-link-form.component.scss'],
  standalone: false,
})
export class InboxInsertPaymentLinkFormComponent implements OnInit {
  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);

  paymentUrl: string;
  paymentLinkForm = this.formBuilder.group({
    dueDate: [this.getFutureDate(10), [Validator.isValidDate]],
    lineItems: this.formBuilder.array([]),
  });
  defaultLineItem = {
    quantity: [1],
    description: ['', [Validators.required]],
    amount: [0, [Validators.min(1)]],
  };
  currencyCode = 'USD'; // fallback to USD if no currency is set.
  subTotal = 0;
  tax = 0;
  currencySymbol = '$';

  showTaxCalculation = false;

  readonly CENTS_PER_DOLLAR = 100;
  readonly DIGITS_INFO = '1.2-2';

  isLoading = signal(false);

  private readonly partnerId$ = inject(PARTNER_ID_TOKEN);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: UntypedFormBuilder,
    private merchantService: MerchantService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    public dialogRef: MatDialogRef<InboxInsertPaymentLinkFormComponent>,
    private inboxService: InboxService,
  ) {}

  ngOnInit(): void {
    this.initCurrency();
    this.lineItems.push(this.formBuilder.group(this.defaultLineItem));
    // TODO: Remove this once we have the tax rates management feature in the Business App.
    this.showTaxCalculation = this.inboxService.isPartnerCenter;
  }

  private initCurrency(): void {
    // TODO: SMBs don't have merchant retail config currency setting.
    //  SMBs will use their partner's currency setting
    //  until we have this feature built in the billing µservice,
    this.partnerId$
      .pipe(
        switchMap((partnerId) => {
          return this.merchantService.getMultiRetailConfigurations(partnerId, ['default']);
        }),
        take(1),
      )
      .subscribe((config) => {
        this.currencyCode = config.get('default').currencyCode;
        this.currencySymbol = getCurrencySymbol(this.currencyCode);
      });
  }

  futureDateFilter = (m: moment.Moment): boolean => {
    return m > moment();
  };

  private getFutureDate(days: number): Date {
    const date = this.normalizeDate(new Date());
    date.setDate(date.getDate() + days);
    return date;
  }

  private normalizeDate(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0));
  }

  get dueDate(): Date {
    return this.paymentLinkForm.controls['dueDate'].value as Date;
  }

  get lineItems(): UntypedFormArray {
    return this.paymentLinkForm.controls['lineItems'] as UntypedFormArray;
  }

  public addLineItem(): void {
    this.lineItems.push(this.formBuilder.group(this.defaultLineItem));
  }

  public deleteLineItemAndCalculate(index: number): void {
    this.lineItems.removeAt(index);
    this.calculateSubTotalAndTax();
  }

  public getLineItemsFormArray(): UntypedFormArray {
    return this.paymentLinkForm.get('lineItems') as UntypedFormArray;
  }

  public getLineItemsFormControl(): AbstractControl[] {
    return this.getLineItemsFormArray().controls;
  }

  private formatAmount(amount: number): string {
    return isNaN(amount) ? '0' : (amount / this.CENTS_PER_DOLLAR).toFixed(2);
  }

  public getLineItemsArray(): PaymentLinkItem[] {
    const lineItems: PaymentLinkItem[] = [];
    this.getLineItemsFormControl().forEach((formControl, idx) => {
      const amount = parseInt((formControl.value.amount * this.CENTS_PER_DOLLAR).toFixed(0), 10);
      const formGroup: UntypedFormGroup = this.getLineItemsFormArray()?.at(idx) as UntypedFormGroup;
      formGroup.controls.amount.setValue(this.formatAmount(amount));
      lineItems.push({
        quantity: 1, // always 1 per line item.
        description: formControl.value.description,
        // convert to cents
        amount: amount,
      } as PaymentLinkItem);
    });
    return lineItems;
  }

  public calculateSubTotalAndTax(): void {
    this.subTotal = this.getLineItemsArray().reduce((acc, cur) => acc + cur.amount, 0);
    if (this.showTaxCalculation) {
      this.hostAppInterface
        .calculatePaymentLinkTax(this.data.conversationDetail, this.subTotal)
        .pipe(take(1))
        .subscribe((taxAmount) => {
          this.tax = taxAmount;
        });
    }
  }

  public requestPayment(): void {
    this.isLoading.set(true);
    if (this.paymentLinkForm.invalid) {
      this.paymentLinkForm.markAllAsTouched();
      this.isLoading.set(false);
      return;
    }
    const normalizedDueDate = this.normalizeDate(this.dueDate);
    this.hostAppInterface
      .createPaymentLink(this.data.conversationDetail, this.getLineItemsArray(), normalizedDueDate)
      .pipe(
        take(1),
        catchError((_) => of('')),
      )
      .subscribe((paymentUrl) => {
        if (paymentUrl) {
          this.paymentUrl = paymentUrl;
          this.dialogRef.close(paymentUrl);
          const successMessage = this.translateService.instant('INBOX.REQUEST_PAYMENT.INVOICE_CREATE_SUCCESS');
          if (this.showTaxCalculation) {
            this.snackbarService.openWithOptions(successMessage, {
              action: this.translateService.instant('INBOX.REQUEST_PAYMENT.VIEW_INVOICES'),
              actionCallback: () => window.open('/invoices', '_blank'),
            });
          } else {
            this.snackbarService.openSuccessSnack(successMessage);
          }
        } else {
          this.snackbarService.openErrorSnack('INBOX.REQUEST_PAYMENT.INVOICE_CREATE_ERROR');
        }
        this.isLoading.set(false);
      });
  }
}
